import { useCallback, useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import type { FeedTokenStatusResponse } from '@awwyeah/shared';
import { feedUrlFromToken, fetchFeedTokenStatus, mintFeedToken, revokeFeedToken } from './feedApi';

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * "Calendar feed" settings card (WS-22) — the outbound half of the Phase 4 calendar
 * bridge. Mints/revokes this member's personal ICS subscribe URL and shows Google/iPhone
 * how to add it. The URL itself is shown exactly once, right after minting: the server
 * never re-sends it (only the SHA-256 hash is persisted, mirroring how session cookies
 * are handled), so this is genuinely the only chance to copy it — a "regenerate" is what
 * you reach for if you lose it or think it leaked.
 */
export function CalendarFeedSection(): JSX.Element {
  const [status, setStatus] = useState<FeedTokenStatusResponse | 'loading'>('loading');
  const [mintedUrl, setMintedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    fetchFeedTokenStatus()
      .then(setStatus)
      .catch(() => setStatus({ exists: false, createdAt: null, lastFetchedAt: null }));
  }, []);

  useEffect(() => refresh(), [refresh]);

  const generate = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const token = await mintFeedToken();
      setMintedUrl(feedUrlFromToken(token));
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate a feed link.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await revokeFeedToken();
      setMintedUrl(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not turn off the feed link.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (!mintedUrl) return;
    try {
      await navigator.clipboard.writeText(mintedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select and copy the link manually.');
    }
  };

  const exists = status !== 'loading' && status.exists;

  return (
    <section
      className="space-y-3 rounded-lg border border-line p-4"
      data-testid="settings-calendar-feed"
    >
      <h2 className="text-lg font-semibold text-fg">Calendar feed</h2>
      <p className="text-xs leading-snug text-fg-3">
        Get a private link that Google Calendar or your phone's calendar app can subscribe to, so
        what's here also shows up there — one-way, read-only, just for you.
      </p>

      {/* Always open, not tucked in a disclosure: this is the part people came here for. */}
      <div className="space-y-2" data-testid="feed-google-steps">
        <h3 className="text-sm font-semibold text-fg-2">Put it on your Google Calendar</h3>
        <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-snug text-fg-2">
          <li>
            Tap <span className="font-semibold">Generate link</span> below, then copy the link.
            It&apos;s only shown once.
          </li>
          <li>
            On a computer (not the phone app — Google only allows this step on the web), open{' '}
            <a
              href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-accent underline"
            >
              Google Calendar → Add calendar → From URL
            </a>{' '}
            while signed in to your Google account.
          </li>
          <li>
            Paste the link into <span className="font-semibold">URL of calendar</span> and click{' '}
            <span className="font-semibold">Add calendar</span>. It appears under &ldquo;Other
            calendars&rdquo;.
          </li>
          <li>
            To see it on your phone too: Google Calendar app → menu → Settings → tap the new
            calendar (under <span className="font-semibold">Show more</span> if it&apos;s hidden) →
            turn on <span className="font-semibold">Sync</span>.
          </li>
        </ol>
        <p className="text-xs leading-snug text-fg-3">
          After that, anything added here shows up in Google by itself. Google checks for changes on
          its own schedule — often a few hours, sometimes up to a day, never instantly. That&apos;s
          their limit, not ours. &ldquo;Last pulled&rdquo; below tells you when Google last looked.
        </p>
      </div>

      {mintedUrl && (
        <div className="space-y-1 rounded-md border border-line bg-bg-2 p-2.5">
          <p className="text-xs font-medium text-fg-2">
            Copy this now — treat it like a password, and it won't be shown again.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={mintedUrl}
              data-testid="feed-url"
              onFocus={(e) => e.currentTarget.select()}
              className="h-10 min-w-0 flex-1 truncate rounded-md border border-line bg-bg px-2 text-xs text-fg"
            />
            <button
              type="button"
              onClick={() => void copy()}
              data-testid="feed-copy"
              className="flex h-10 min-w-[44px] shrink-0 items-center justify-center rounded-md border border-line px-2 text-fg-2"
              aria-label="Copy feed link"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-1 text-sm">
        <Row label="Status" value={status === 'loading' ? 'Checking…' : exists ? 'On' : 'Off'} />
        {exists && status.lastFetchedAt && (
          <Row label="Last pulled" value={relativeTime(status.lastFetchedAt)} />
        )}
        {exists && !status.lastFetchedAt && (
          <Row label="Last pulled" value="Not yet — finish the steps above" />
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={() => void generate()}
          disabled={busy}
          data-testid="feed-generate"
          className="min-h-[44px] rounded-xl bg-inverse px-4 py-2 text-sm font-semibold text-inverse-fg disabled:opacity-60"
        >
          {busy ? 'Working…' : exists ? 'Regenerate link' : 'Generate link'}
        </button>
        {exists && (
          <button
            type="button"
            onClick={() => void revoke()}
            disabled={busy}
            data-testid="feed-revoke"
            className="min-h-[44px] rounded-xl border border-line px-4 py-2 text-sm font-semibold text-fg-2 disabled:opacity-60"
          >
            Turn off
          </button>
        )}
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}

      <details className="pt-1 text-sm text-fg-2">
        <summary className="cursor-pointer select-none font-medium">
          iPhone or Apple Calendar instead
        </summary>
        <div className="mt-2 space-y-2 text-xs leading-snug text-fg-3">
          <p>
            Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar → paste
            the link.
          </p>
          <p>Apple refreshes subscribed calendars on its own schedule too.</p>
        </div>
      </details>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-fg-3">{label}</span>
      <span className="font-medium text-fg">{value}</span>
    </div>
  );
}
